import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { setAlert } from '../../../redux/dashboard/actions';

import components from '../../../components';
import Mapping from '../mapping';
import ImportModal from '../importModal';
const { SearchBox, DropdownSearch, CustomButton, CustomInput, EmptyState } = components;

import API from '../../../services/api';

import utils from '../../../utils';
import { initializeMapping } from '../../../utils/integrations';
import { getSelectedProject } from '../../../redux/projects/actions';

const MappingsTab = ({
  selectedIntegration,
  mappings,
  credentials,
  assets,
  loadCredentials,
  loading,
  loadMappings,
  isCreateModalOpen,
  isImportModalOpen,
  closeModal
}) => {
  const [filteredMappings, setFilteredMappings] = useState(mappings);
  const [selectedCredential, setSelectedCredential] = useState('');
  const [values, setValues] = useState({});
  const [displayName, setDisplayName] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);

  const project = useSelector(getSelectedProject);
  const dispatch = useDispatch();

  useEffect(() => {
    setFilteredMappings(mappings);
    if (selectedIntegration && selectedIntegration?.mappingMetadataFields) {
      setValues(initializeMapping(selectedIntegration.mappingMetadataFields));
    } else {
      setValues({});
    }
  }, [mappings]);

  const searchMappings = searchBy => {
    if (searchBy !== '') {
      setFilteredMappings(
        mappings.filter(mapping => {
          const search = searchBy.toUpperCase();
          const asset = assets.find(item => item.id === mapping.assetId);
          return [mapping.assetId, asset?.displayName, mapping.playerId].some(value =>
            value?.toUpperCase().includes(search)
          );
        })
      );
    } else {
      setFilteredMappings(mappings);
    }
  };

  const createMapping = () => {
    if (!selectedCredential?.id || !selectedAsset?.id) {
      dispatch(
        setAlert(utils.generateAlert('Could not create Mapping! Select an Asset and Credential.', 'error'))
      );
      return;
    }

    API.createMapping(project.id, {
      credentialId: selectedCredential.id,
      assetId: selectedAsset.id,
      vendor: selectedIntegration.vendor,
      metadata: values,
      displayName: displayName ? displayName : ''
    })
      .then(() => {
        dispatch(setAlert(utils.generateAlert('Mapping created successfuly!', 'success')));
        loadCredentials();
        loadMappings();
        resetForm();
      })
      .catch(err => {
        console.error(err);
        dispatch(setAlert(utils.generateAlert('Could not create Mapping! Please try again.', 'error')));
      });
  };

  const updateForm = (field, val) => {
    setValues({ ...values, [field]: val });
  };

  const resetForm = () => {
    setDisplayName('');
    setSelectedAsset(null);
    setSelectedCredential('');
    setValues(initializeMapping(selectedIntegration.mappingMetadataFields));
    closeModal();
  };

  const renderLoading = () => {
    return <p>Loading...</p>;
  };

  const renderContent = () => {
    if (!project.enabledCmsIntegrations.includes(selectedIntegration.vendor)) return renderNoCredentialState();
    return filteredMappings.length > 0 ? renderMappingList() : renderEmptyState();
  };

  const renderMappingList = () => {
    return (
      <>
        <div>
          <SearchBox placeholder="Search by Asset or Player ID" className="outline" onChange={searchMappings} />
          {filteredMappings.length > 0 ? (
            <>
              {filteredMappings.map(mapping => {
                const asset = assets.find(item => item.id === mapping.assetId) || {
                  id: mapping.assetId,
                  displayName: mapping.assetId
                };
                const credential = credentials.find(credential => credential.id === mapping.credentialId);
                return (
                  <Mapping
                    key={mapping.id}
                    mapping={mapping}
                    asset={asset}
                    credentialName={credential && credential.displayName ? credential.displayName : ''}
                    loadCredentials={loadCredentials}
                    loadMappings={loadMappings}
                    selectedIntegration={selectedIntegration}
                  />
                );
              })}
            </>
          ) : (
            <div className="no-results-found">
              <p>No mappings were found</p>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderEmptyState = () => {
    return (
      <EmptyState
        icon={'deviceOutlineDark'}
        title={`You don't have any mapping.`}
        details={`Start mapping your assets by clicking "Add CMS Mapping".`}
      />
    );
  };

  const renderNoCredentialState = () => {
    return (
      <EmptyState
        icon={'addUser'}
        title={`You don't have any credentials for this integration.`}
        details={`Create your first credential by clicking "Add Account" on the Accounts tab.`}
      />
    );
  };

  return (
    assets && (
      <div>
        {loading ? renderLoading() : renderContent()}
        <Modal
          size="lg"
          isOpen={isCreateModalOpen}
          fade={false}
          centered
          toggle={resetForm}
          className="integration-create-modal"
        >
          <ModalHeader className="header-gray" tag="div">
            <h5>Add CMS Mapping</h5>
          </ModalHeader>
          <ModalBody>
            <div className="account-fields">
            <div className="form-row">
              <div className="col" key="mapping-name">
                <div className="form-group">
                  <label>Name</label>
                  <CustomInput
                    id="mapping-name"
                    type="text"
                    value={displayName}
                    isEditable
                    onChange={e => {
                      setDisplayName(e.target.value);
                    }}
                  />
                </div>
              </div>
              <div className="col" key="asset-id">
                <div className="form-group">
                  <label>Asset</label>
                  <DropdownSearch
                    title={'Search Asset'}
                    selected={
                      selectedAsset
                        ? { label: selectedAsset.displayName || selectedAsset.id, value: selectedAsset.id }
                        : { label: 'Select Asset', value: '' }
                    }
                    leftAlign
                    onSelect={setSelectedAsset}
                    options={assets.map(asset => ({ label: asset.displayName || asset.id, value: asset }))}
                  />
                </div>
              </div>

              <div className="col" key="credential-id">
                <div className="form-group">
                  <label>Credential</label>
                  <DropdownSearch
                    title={'Search Solution Config'}
                    selected={
                      selectedCredential
                        ? { label: selectedCredential.displayName, value: selectedCredential.id }
                        : { label: 'Select credential', value: '' }
                    }
                    leftAlign
                    onSelect={value => {
                      setSelectedCredential(credentials.find(cred => cred.id === value));
                    }}
                    options={
                      credentials
                        ? credentials.map(credential => {
                            return { label: credential.displayName, value: credential.id };
                          })
                        : []
                    }
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              {selectedIntegration.mappingMetadataFields.map(field => {
                return (
                  <div className="col" key={field}>
                    <div className="form-group">
                      <label>{field}</label>
                      <CustomInput
                        id={`new-mappings-${field}`}
                        type="text"
                        value={values[field]}
                        isEditable
                        onChange={e => {
                          updateForm(field, e.target.value);
                        }}
                      />
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <CustomButton classes="btn-secondary" title="Cancel" handleClick={resetForm} />
            <CustomButton classes="btn-primary" title="Create" handleClick={createMapping} />
          </ModalFooter>
        </Modal>
        <ImportModal
          isOpen={isImportModalOpen}
          customColumns={selectedIntegration.mappingMetadataFields.map(field => field)}
          integrationVendor={selectedIntegration.vendor}
          onUpdate={() => {
            loadCredentials();
            loadMappings();
          }}
          toggleModal={closeModal}
        />
      </div>
    )
  );
};

export default MappingsTab;
